import React from 'react'
import { TFunction, useTranslation } from 'react-i18next'

import { postTraining } from '../../../api/training'
import { ReactComponent as Analytics } from '../../../assets/icons/analytics.svg'
import { ReactComponent as ChatBubble } from '../../../assets/icons/chatbubble.svg'
import { ReactComponent as ChatbubbleEllipses } from '../../../assets/icons/chatbubble-ellipses.svg'
import { ReactComponent as ChatBubbles } from '../../../assets/icons/chatbubbles.svg'
import { ReactComponent as ActionsIcon } from '../../../assets/icons/flask.svg'
import { ReactComponent as Folder } from '../../../assets/icons/folder.svg'
import { ReactComponent as Library } from '../../../assets/icons/library.svg'
import { ReactComponent as ListCircle } from '../../../assets/icons/list-circle.svg'
import { ReactComponent as Redirect } from '../../../assets/icons/redirect.svg'
import { ReactComponent as Tools } from '../../../assets/icons/tools.svg'
import { ReactComponent as UserSettings } from '../../../assets/icons/user-settings.svg'
import { Mode } from '../../../types/mode'
import { IRoleEnum } from '../../../types/router'
import { hasPermission } from '../../../utils/auth'
import { hasMode } from '../../../utils/isMode'
import TrainingButton from '../../StatusButton/StatusButton'

import Language from './Language/Language'
import MenuItem, { IMenuItem } from './MenuItem/MenuItem'
import Profile from './Profile/Profile'

import styles from './menu.module.scss'

interface IMenuCategory {
    title: string
    role: IRoleEnum[]
    menuItems: IMenuItem[]
}

const menuCategories = (
    t: TFunction<'translation', undefined>
): IMenuCategory[] => {
    return [
        {
            title: t('training'),
            role: [IRoleEnum.USER, IRoleEnum.ADMIN],
            menuItems: [
                {
                    title: t('nlu_data'),
                    url: '/training-data/data',
                    image: Library,
                    role: [IRoleEnum.USER, IRoleEnum.ADMIN],
                    modes: [Mode.ENHANCED],
                },
                {
                    title: t('responses'),
                    url: '/responses',
                    image: ChatBubble,
                    role: [IRoleEnum.USER, IRoleEnum.ADMIN],
                    modes: [Mode.ENHANCED],
                },
                {
                    title: t('rules'),
                    url: '/rules',
                    image: ListCircle,
                    role: [IRoleEnum.USER, IRoleEnum.ADMIN],
                    modes: [Mode.ENHANCED],
                },
                {
                    title: t('slots_forms'),
                    url: '/slots-forms',
                    image: Folder,
                    role: [IRoleEnum.USER, IRoleEnum.ADMIN],
                    modes: [Mode.ENHANCED],
                },
                {
                    title: t('actions'),
                    url: '/actions',
                    image: ActionsIcon,
                    role: [IRoleEnum.USER, IRoleEnum.ADMIN],
                    modes: [Mode.ENHANCED],
                },
                {
                    title: t('configuration'),
                    url: '/config',
                    image: Tools,
                    role: [IRoleEnum.USER, IRoleEnum.ADMIN],
                    modes: [Mode.ENHANCED],
                },
                {
                    title: 'FAQ',
                    url: '/faq',
                    image: ChatBubble,
                    role: [IRoleEnum.USER, IRoleEnum.ADMIN],
                    modes: [Mode.BASIC, Mode.ENHANCED],
                },
            ],
        },
        {
            title: t('analytics'),
            role: [IRoleEnum.USER, IRoleEnum.ADMIN],
            menuItems: [
                {
                    title: t('conversations'),
                    url: '/conversations',
                    image: ChatBubbles,
                    role: [IRoleEnum.USER, IRoleEnum.ADMIN],
                    modes: [Mode.BASIC, Mode.ENHANCED],
                },
                {
                    title: t('messages'),
                    url: '/messages',
                    image: ChatbubbleEllipses,
                    role: [IRoleEnum.USER, IRoleEnum.ADMIN],
                    modes: [Mode.ENHANCED],
                },
                {
                    title: t('analytics'),
                    url: '/analytics',
                    image: Analytics,
                    role: [IRoleEnum.USER, IRoleEnum.ADMIN],
                    modes: [Mode.BASIC, Mode.ENHANCED],
                },
            ],
        },
        {
            title: t('admin'),
            role: [IRoleEnum.ADMIN],
            menuItems: [
                {
                    title: t('users'),
                    url: '/users',
                    image: UserSettings,
                    role: [IRoleEnum.ADMIN],
                    modes: [Mode.BASIC, Mode.ENHANCED],
                },
                {
                    title: t('webhooks'),
                    url: '/webhooks',
                    image: Redirect,
                    role: [IRoleEnum.ADMIN],
                    modes: [Mode.BASIC, Mode.ENHANCED],
                },
                {
                    title: t('data_sources'),
                    url: '/data-sources',
                    image: Folder,
                    role: [IRoleEnum.ADMIN],
                    modes: [Mode.BASIC, Mode.ENHANCED],
                },
            ],
        },
    ]
}

function Menu() {
    const { t } = useTranslation()

    return (
        <div className={styles.menu}>
            <Profile />
            <nav className={styles.menu__nav}>
                {menuCategories(t).map(({ title, menuItems, role }) =>
                    hasPermission(role) ? (
                        <React.Fragment key={title}>
                            <p className={styles.menu__categoryTitle}>
                                {title}
                            </p>
                            {menuItems.map((menuItem) =>
                                hasPermission(menuItem.role) &&
                                hasMode(menuItem.modes) ? (
                                    <MenuItem
                                        key={menuItem.title}
                                        menuItem={menuItem}
                                    />
                                ) : null
                            )}
                        </React.Fragment>
                    ) : null
                )}
                {hasPermission([IRoleEnum.ADMIN]) ? (
                    <TrainingButton
                        title={t('train')}
                        submitRequest={postTraining}
                    />
                ) : null}
            </nav>
            <Language />
        </div>
    )
}

export default Menu
